import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { PolarArea } from 'react-chartjs-2';
import Slider from 'react-input-slider';
import instructChar from '../images/instruct-t.webp';
import typingSound from '../sounds/escribir.mp3';

const SLIDER_COLORS = {
    sweetness: '#c9a96e',
    acidity:   '#d4a43e',
    tannin:    '#8b1a1a',
    body:      '#6b1e3d',
    finish:    '#1d9a78',
};

const isMobile = () => window.innerWidth <= 640;

const sliderStyles = (color) => {
    const mobile = isMobile();
    return {
        track:  { background: 'rgba(232,220,196,0.10)', borderRadius: mobile ? 6 : 4, height: mobile ? 10 : 6 },
        active: { background: color },
        thumb:  {
            width:  mobile ? 28 : 18,
            height: mobile ? 28 : 18,
            background: '#e8dcc4',
            border: `2px solid ${color}`,
            boxShadow: `0 0 8px ${color}88`,
        },
    };
};

const NumericInputSlide = ({ actual, setCanGoNext }) => {
    const { t } = useTranslation();

    const getInitialSliderValues = () => {
        const savedValues = JSON.parse(localStorage.getItem('selected6'));
        return savedValues || { a: 0, b: 0, c: 0, d: 0, e: 0 };
    };

    const [sliderValues, setSliderValues] = useState(getInitialSliderValues);
    const [sliderValue,  setSliderValue]  = useState(sliderValues.a);
    const [sliderValue2, setSliderValue2] = useState(sliderValues.b);
    const [sliderValue3, setSliderValue3] = useState(sliderValues.c);
    const [sliderValue4, setSliderValue4] = useState(sliderValues.d);
    const [sliderValue5, setSliderValue5] = useState(sliderValues.e);
    const [title, setTitle] = useState(null);
    const [displayedText, setDisplayedText] = useState('');
    const [typingActive, setTypingActive] = useState(false);

    const overlayRef = useRef(null);
    const audioRef = useRef(null);

    const handleSliderChange  = (value) => { setSliderValue(value);  setSliderValues(prev => ({ ...prev, a: value })); };
    const handleSliderChange2 = (value) => { setSliderValue2(value); setSliderValues(prev => ({ ...prev, b: value })); };
    const handleSliderChange3 = (value) => { setSliderValue3(value); setSliderValues(prev => ({ ...prev, c: value })); };
    const handleSliderChange4 = (value) => { setSliderValue4(value); setSliderValues(prev => ({ ...prev, d: value })); };
    const handleSliderChange5 = (value) => { setSliderValue5(value); setSliderValues(prev => ({ ...prev, e: value })); };

    const data = {
        labels: ['', '', '', '', ''],
        datasets: [
            { label: t('taste.acidity_label'),   data: [sliderValue2 + 1, 0, 0, 0, 0], backgroundColor: 'rgba(210,164,62,0.45)',  borderColor: 'rgba(210,164,62,0.85)',  borderWidth: 1 },
            { label: t('taste.tannin_label'),    data: [0, sliderValue3 + 1, 0, 0, 0], backgroundColor: 'rgba(139,26,26,0.45)',   borderColor: 'rgba(139,26,26,0.85)',   borderWidth: 1 },
            { label: t('taste.body_label'),      data: [0, 0, sliderValue4 + 1, 0, 0], backgroundColor: 'rgba(107,30,61,0.45)',   borderColor: 'rgba(107,30,61,0.85)',   borderWidth: 1 },
            { label: t('taste.finish_label'),    data: [0, 0, 0, sliderValue5 + 1, 0], backgroundColor: 'rgba(29,154,120,0.45)', borderColor: 'rgba(29,154,120,0.85)',  borderWidth: 1 },
            { label: t('taste.sweetness_label'), data: [0, 0, 0, 0, sliderValue + 1],  backgroundColor: 'rgba(201,169,110,0.45)', borderColor: 'rgba(201,169,110,0.85)', borderWidth: 1 },
        ],
    };

    const options = {
        scales: {
            r: {
                angleLines: { color: 'rgba(232,220,196,0.18)' },
                grid:        { color: 'rgba(232,220,196,0.10)' },
                suggestedMin: 0,
                suggestedMax: 3,
                ticks: { display: false },
                pointLabels: { display: false, font: { size: 0 } },
            },
        },
        plugins: {
            legend: false,
            tooltip: {
                backgroundColor: 'rgba(20,5,15,0.92)',
                titleColor: '#e8dcc4',
                bodyColor: '#c9a96e',
                borderColor: 'rgba(232,220,196,0.18)',
                borderWidth: 1,
                padding: 8,
                callbacks: { label: (ctx) => ctx.dataset.label },
            },
        },
        animation: { duration: 400 },
    };

    useEffect(() => {
        const savedTest = JSON.parse(localStorage.getItem('currentTest') || '[]');
        setTitle(savedTest.title);
        localStorage.setItem('selected6', JSON.stringify(sliderValues));
    }, [sliderValues]);

    useEffect(() => {
        if (setCanGoNext) setCanGoNext(true);
    }, [setCanGoNext]);

    useEffect(() => {
        const overlay = overlayRef.current;
        if (!overlay) return;
        const observer = new MutationObserver(() => {
            if (overlay.style.display !== 'none') {
                setDisplayedText('');
                setTypingActive(true);
            }
        });
        observer.observe(overlay, { attributes: true, attributeFilter: ['style'] });
        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!typingActive) return;
        const fullText = t('taste.bubble');
        if (displayedText.length >= fullText.length) {
            setTypingActive(false);
            if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
            return;
        }
        if (audioRef.current && audioRef.current.paused) {
            audioRef.current.volume = 0.3;
            audioRef.current.loop = true;
            audioRef.current.play().catch(() => {});
        }
        const timeout = setTimeout(() => {
            setDisplayedText(fullText.slice(0, displayedText.length + 1));
        }, 28);
        return () => clearTimeout(timeout);
    }, [typingActive, displayedText, t]);

    const handleQuitInstructions = () => {
        const instructionsDiv = overlayRef.current;
        if (instructionsDiv) instructionsDiv.style.display = 'none';
        setTypingActive(false);
        if (audioRef.current) { audioRef.current.pause(); audioRef.current.currentTime = 0; }
    };

    const sweetness = t('taste.sweetness', { returnObjects: true });
    const acidity   = t('taste.acidity',   { returnObjects: true });
    const tannin    = t('taste.tannin',    { returnObjects: true });
    const body      = t('taste.body',      { returnObjects: true });
    const finish    = t('taste.finish',    { returnObjects: true });

    const rows = [
        { key: 'sweetness', label: t('taste.sweetness_label'), value: sliderValue,  values: sweetness, color: SLIDER_COLORS.sweetness, onChange: handleSliderChange  },
        { key: 'acidity',   label: t('taste.acidity_label'),   value: sliderValue2, values: acidity,   color: SLIDER_COLORS.acidity,   onChange: handleSliderChange2 },
        { key: 'tannin',    label: t('taste.tannin_label'),    value: sliderValue3, values: tannin,    color: SLIDER_COLORS.tannin,    onChange: handleSliderChange3 },
        { key: 'body',      label: t('taste.body_label'),      value: sliderValue4, values: body,      color: SLIDER_COLORS.body,      onChange: handleSliderChange4 },
        { key: 'finish',    label: t('taste.finish_label'),    value: sliderValue5, values: finish,    color: SLIDER_COLORS.finish,    onChange: handleSliderChange5 },
    ];

    return (
        <div className="slide-container">
            <audio ref={audioRef} src={typingSound} />
            <div className="overlay" id="instructions-div" ref={overlayRef}>
                <div className="vi-instruct-scene">
                    <img src={instructChar} alt="" className="vi-instruct-char" />
                    <div className="vi-instruct-bubble-wrap">
                        <div className="vi-bubble-row">
                            <svg className="vi-tail-svg" width="28" height="20" viewBox="0 0 28 20">
                                <polygon points="28,0 28,20 0,10" fill="#fff" stroke="#111" strokeWidth="3" strokeLinejoin="round"/>
                            </svg>
                            <div className="vi-bubble-shell">
                                <div className="vi-instruct-bubble">
                                    <p className="vi-instruct-text">{displayedText}<span className="vi-instruct-cursor">|</span></p>
                                </div>
                                <button className="vi-instruct-close" onClick={handleQuitInstructions} aria-label={t('common.close')}>×</button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="ts-phase-icon" role="img" aria-label={t('stats.taste_phase')}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M7 6 L17 6 Q18 6 18 13 Q18 21 12 22 Q6 21 6 13 Q6 6 7 6 Z" fill="currentColor" fillOpacity="0.22" strokeWidth="1.5" strokeLinejoin="round"/>
                    <line x1="12" y1="6" x2="12" y2="20" strokeWidth="1.3" strokeOpacity="0.7"/>
                </svg>
                <span className="ts-phase-icon-tip">{t('stats.taste_phase')}</span>
            </div>
            <div className="actual-wine">{title}</div>

            <div className="gt-phase">
                <div className="gt-title"><span>{t('taste.title')}</span></div>

                <div className="gt-content">
                    <div className="gt-sliders">
                        {rows.map(({ key, label, value, values, color, onChange }) => (
                            <div key={key} className="gt-row">
                                <div className="gt-row-label" style={{ '--gt-color': color }}>
                                    {label}
                                </div>
                                <div className="gt-row-right">
                                    <div className="gt-slider-wrap">
                                        <Slider
                                            axis="x"
                                            x={value}
                                            xmax={2}
                                            onChange={({ x }) => onChange(x)}
                                            styles={sliderStyles(color)}
                                        />
                                    </div>
                                    <div className="gt-row-value" style={{ color }}>
                                        {values[value]}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="gt-chart">
                        <PolarArea data={data} options={options} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NumericInputSlide;
